/**
 * Text rendering — bakes each text node's string into a 2D canvas on first
 * frame, uploads to a GPU texture, and the rect pipeline samples it with the
 * same SDF mask + shadow as plain rects.
 *
 * Layer 3 extension: word-wrap with line-height + letter-spacing, alignment,
 * and ellipsis overflow. The bake step is more sophisticated — it computes
 * lines from the node's w/h, draws each line, and uploads a single tall
 * canvas (height = lineHeight * fontSize * lines).
 *
 * Why this is fine for the PoC:
 *  - Each text node gets ONE canvas → ONE texture → ONE draw call.
 *  - The cache key is (id, text, fontSize, weight, family, color, wrap, w,
 *    h, lineHeight, letterSpacing, align, ellipsis). Updates re-bake; no
 *    updates = cache hit.
 *  - Browser's 2D canvas gives us free subpixel-AA at the rendered font size.
 *  - Same anti-aliased fragment shader masks the result with the rounded-rect
 *    SDF so corners, shadows, and blur still apply.
 */

import type { SceneNode } from "./sceneGraph";
import { bakePathToCanvas } from "./svgPath";

interface TextEntry {
  texture: WebGLTexture;
  width: number;     // canvas px
  height: number;    // canvas px
  cssWidth: number;  // measured in CSS px for layout
  cssHeight: number;
  /** Key used last time; if node changed, re-bake. */
  key: string;
}

const cache = new Map<number, TextEntry>();

function keyOf(n: SceneNode): string {
  const fill = (n.textColor ?? n.fill);
  const r = Math.round(fill.r * 255);
  const g = Math.round(fill.g * 255);
  const b = Math.round(fill.b * 255);
  const a = Math.round(fill.a * 255);
  return [
    n.type,
    n.text ?? "",
    n.fontSize ?? 0,
    n.fontWeight ?? 0,
    n.fontFamily ?? "",
    r, g, b, a,
    n.w,
    n.h,
    n.textWrap ? "wrap" : "nowrap",
    n.lineHeight ?? 0,
    n.letterSpacing ?? 0,
    n.textAlign ?? "left",
    n.textOverflowEllipsis ? "ellipsis" : "noellipsis",
  ].join("|");
}

/** Word-wrap a string to fit a CSS-px width using the given font. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  letterSpacing: number,
): string[] {
  const lines: string[] = [];
  if (!text) return lines;
  // For multi-paragraph, split on \n first.
  const paragraphs = text.split(/\n/);
  for (const para of paragraphs) {
    if (!para) {
      lines.push("");
      continue;
    }
    const words = para.split(/\s+/).filter(Boolean);
    let current = "";
    for (const w of words) {
      const candidate = current ? current + " " + w : w;
      const measured = measureWithSpacing(ctx, candidate, letterSpacing);
      if (measured <= maxWidth || !current) {
        current = candidate;
      } else {
        lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

function measureWithSpacing(
  ctx: CanvasRenderingContext2D,
  text: string,
  letterSpacing: number,
): number {
  const m = ctx.measureText(text);
  let w = m.width;
  if (letterSpacing && text.length > 1) {
    w += letterSpacing * (text.length - 1);
  }
  return w;
}

function renderToCanvas(n: SceneNode): { canvas: HTMLCanvasElement; cssW: number; cssH: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2.0);
  const sizePx = (n.fontSize ?? 16) * dpr;
  const weight = n.fontWeight ?? 400;
  const family = n.fontFamily ?? "system-ui, -apple-system, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif";
  const text = n.text ?? "";
  const fillColor = n.textColor ?? n.fill;
  const lineHeight = (n.lineHeight ?? 1.2) * sizePx;
  const letterSpacing = (n.letterSpacing ?? 0) * dpr;
  const wrap = !!n.textWrap;
  const align = n.textAlign ?? "left";
  const ellipsis = !!n.textOverflowEllipsis;
  const maxCssW = n.w - 16; // padding inside rect
  const maxCssH = n.h - 12;

  // Probe context for measurements.
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.font = `${weight} ${sizePx}px ${family}`;

  let lines: string[];
  if (wrap) {
    // Apply ellipsis if needed.
    const rawLines = wrapText(probe, text, maxCssW, n.letterSpacing ?? 0);
    if (ellipsis) {
      // Trim lines that exceed the height, append ….
      const maxLines = Math.max(1, Math.floor(maxCssH / ((n.lineHeight ?? 1.2) * (n.fontSize ?? 16))));
      if (rawLines.length > maxLines) {
        lines = rawLines.slice(0, maxLines);
        // Ellipsize the last visible line if it doesn't fit.
        const lastLine = lines[lines.length - 1];
        const probe2 = document.createElement("canvas").getContext("2d")!;
        probe2.font = `${weight} ${sizePx}px ${family}`;
        while (
          lines.length === maxLines &&
          measureWithSpacing(probe2, lastLine + "…", n.letterSpacing ?? 0) > maxCssW
        ) {
          if (lastLine.length === 0) break;
          lines[lines.length - 1] = lastLine.slice(0, -1);
          if (lines[lines.length - 1].length === 0) break;
        }
        if (lines[lines.length - 1].length > 0) {
          lines[lines.length - 1] = lines[lines.length - 1] + "…";
        }
      } else {
        lines = rawLines;
      }
    } else {
      lines = rawLines;
    }
  } else {
    lines = text.split(/\n/);
  }

  // Compute canvas size.
  const padX = 4 * dpr;
  const padY = 4 * dpr;
  let widestCss = 0;
  for (const l of lines) {
    const m = measureWithSpacing(probe, l, n.letterSpacing ?? 0);
    if (m > widestCss) widestCss = m;
  }
  const cssW = widestCss + 16; // a bit of slack
  const cssH = lines.length * (n.lineHeight ?? 1.2) * (n.fontSize ?? 16);
  const w = Math.max(2, Math.ceil(cssW * dpr + 2 * padX));
  const h = Math.max(2, Math.ceil(cssH * dpr + 2 * padY));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.font = `${weight} ${sizePx}px ${family}`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = `rgba(${Math.round(fillColor.r * 255)}, ${Math.round(fillColor.g * 255)}, ${Math.round(fillColor.b * 255)}, ${Math.round(fillColor.a * 255)})`;
  // Add letter-spacing via canvas API if supported (it isn't in all browsers,
  // so we draw letter-by-letter as a fallback).
  if (letterSpacing === 0) {
    ctx.textAlign = align;
    const x =
      align === "center" ? w * 0.5 :
      align === "right" ? w - padX :
      padX;
    let cy = padY + lineHeight * 0.5;
    for (const l of lines) {
      ctx.fillText(l, x, cy);
      cy += lineHeight;
    }
  } else {
    // Letter-by-letter fallback.
    let cy = padY + lineHeight * 0.5;
    for (const l of lines) {
      const m = ctx.measureText(l);
      let cursorX = padX;
      if (align === "center") cursorX = (w - m.width - letterSpacing * (l.length - 1)) * 0.5;
      else if (align === "right") cursorX = w - padX - m.width - letterSpacing * (l.length - 1);
      for (const ch of l) {
        ctx.fillText(ch, cursorX, cy);
        cursorX += ctx.measureText(ch).width + letterSpacing;
      }
      cy += lineHeight;
    }
  }

  return { canvas, cssW: cssW + 16 / dpr, cssH: cssH + 12 / dpr };
}

export function getTextTexture(gl: WebGL2RenderingContext, n: SceneNode): TextEntry | null {
  if (n.type !== "text") return null;
  const key = keyOf(n);
  const existing = cache.get(n.id);
  if (existing && existing.key === key) return existing;

  const { canvas, cssW, cssH } = renderToCanvas(n);
  let entry = existing;
  if (!entry) {
    const tex = gl.createTexture();
    if (!tex) return null;
    entry = { texture: tex, width: 0, height: 0, cssWidth: 0, cssHeight: 0, key };
  }
  gl.bindTexture(gl.TEXTURE_2D, entry.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  entry.width = canvas.width;
  entry.height = canvas.height;
  entry.cssWidth = cssW;
  entry.cssHeight = cssH;
  entry.key = key;
  cache.set(n.id, entry);
  return entry;
}

/** Returns the CSS size we measured the text as. Useful when caller wants
 *  to adjust node.w/h to fit the natural text width. */
export function textCssSize(entry: TextEntry): { w: number; h: number } {
  return { w: entry.cssWidth, h: entry.cssHeight };
}

// ---------------------------------------------------------------------------
// SVG path rasterization
// ---------------------------------------------------------------------------

interface PathEntry {
  texture: WebGLTexture;
  width: number;
  height: number;
  cssWidth: number;
  cssHeight: number;
  key: string;
}

const pathCache = new Map<number, PathEntry>();

function pathKeyOf(n: SceneNode): string {
  return [
    n.pathD ?? "",
    n.pathViewBox ? `${n.pathViewBox.x},${n.pathViewBox.y},${n.pathViewBox.w},${n.pathViewBox.h}` : "",
    n.w, n.h,
    n.fill.r.toFixed(4), n.fill.g.toFixed(4), n.fill.b.toFixed(4), n.fill.a.toFixed(4),
  ].join("|");
}

/** Bake an SVG `<path>`'s `d` attribute to a texture. Same caching model as
 *  text: one texture per node, regenerated on key change. */
export function getPathTexture(
  gl: WebGL2RenderingContext,
  n: SceneNode,
): PathEntry | null {
  if (!n.pathD) return null;
  const key = pathKeyOf(n);
  const existing = pathCache.get(n.id);
  if (existing && existing.key === key) return existing;

  let entry = existing;

  const canvas = bakePathToCanvas(
    n.pathD,
    n.w,
    n.h,
    n.fill,
    n.pathViewBox,
  );

  if (!entry) {
    const tex = gl.createTexture();
    if (!tex) return null;
    entry = { texture: tex, width: 0, height: 0, cssWidth: 0, cssHeight: 0, key };
  }
  gl.bindTexture(gl.TEXTURE_2D, entry.texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  entry.width = canvas.width;
  entry.height = canvas.height;
  entry.cssWidth = n.w;
  entry.cssHeight = n.h;
  entry.key = key;
  pathCache.set(n.id, entry);
  return entry;
}

export function clearPathCache(gl: WebGL2RenderingContext): void {
  for (const e of pathCache.values()) gl.deleteTexture(e.texture);
  pathCache.clear();
}

export function clearTextCache(gl: WebGL2RenderingContext): void {
  for (const e of cache.values()) gl.deleteTexture(e.texture);
  cache.clear();
}